import React, { useState, useEffect } from 'react';
import { Paper, Typography, List, ListItem, Box, IconButton, Collapse, Divider, Tooltip, Select, MenuItem, FormControl, Button, Chip } from '@mui/material';
import { Layers, ExpandMore, ExpandLess, Label, CloudUpload, Delete, Visibility, VisibilityOff, PlayArrow, CalendarToday } from '@mui/icons-material';
import { useMap, BasemapType } from '../context/MapContext';
import { getAvailableCollections, type GEECollection } from '../services/geeService';
import GEEDateRangeSelector from './GEEDateRangeSelector';
import GEEAnimation from './GEEAnimation';

interface ImportedLayer {
  id: string;
  name: string;
  geojson: GeoJSON.FeatureCollection;
  visible: boolean;
}

interface LayerControlsProps {
  layers: Array<{
    id: string;
    name: string;
    visible: boolean;
    color?: string;
  }>;
  importedLayers?: ImportedLayer[];
  onImportedLayerToggle?: (layerId: string) => void;
  onImportedLayerDelete?: (layerId: string) => void;
}

const LayerControls: React.FC<LayerControlsProps> = ({ 
  layers, 
  importedLayers = [], 
  onImportedLayerToggle, 
  onImportedLayerDelete 
}) => {
  const [expanded, setExpanded] = useState(true);
  const [importedExpanded, setImportedExpanded] = useState(true);
  const [geeCollections, setGeeCollections] = useState<GEECollection[]>([]);
  const [selectedGeeCollection, setSelectedGeeCollection] = useState<string | null>(null);
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const { visibleLayers, toggleLayer, labelsVisible, setLabelsVisible, basemap, setBasemap } = useMap();

  // Calculate distance for GPX tracks
  const calculateGPXDistance = (geojson: GeoJSON.FeatureCollection): number => {
    let totalDistance = 0;
    
    geojson.features.forEach(feature => {
      if (feature.geometry.type === 'LineString') {
        const coordinates = feature.geometry.coordinates;
        for (let i = 1; i < coordinates.length; i++) {
          const [lon1, lat1] = coordinates[i - 1];
          const [lon2, lat2] = coordinates[i];
          
          // Haversine formula for distance calculation
          const R = 6371; // Earth's radius in km
          const dLat = (lat2 - lat1) * Math.PI / 180;
          const dLon = (lon2 - lon1) * Math.PI / 180;
          const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          totalDistance += R * c;
        }
      }
    });
    
    return totalDistance;
  };

  // Get file extension to determine layer type
  const getLayerType = (filename: string): string => {
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext || 'unknown';
  };

  // Load GEE collections on component mount
  useEffect(() => {
    const loadGeeCollections = async () => {
      try {
        const collections = await getAvailableCollections();
        setGeeCollections(collections);
      } catch (error) {
        console.error('Failed to load GEE collections:', error);
        // Fallback collections if backend fails
        const fallbackCollections = [
          {
            id: 'sentinel-2',
            name: 'Sentinel-2',
            description: 'Sentinel-2 Level 2A surface reflectance',
            collection: 'COPERNICUS/S2_SR_HARMONIZED',
            bands: ['B2', 'B3', 'B4', 'B8'],
            startDate: '2015-06-23',
            cloudFilter: 20
          },
          {
            id: 'landsat-8',
            name: 'Landsat 8',
            description: 'Landsat 8 Collection 2 Tier 1 surface reflectance',
            collection: 'LANDSAT/LC08/C02/T1_L2',
            bands: ['SR_B2', 'SR_B3', 'SR_B4', 'SR_B5'],
            startDate: '2013-04-11',
            cloudFilter: 20
          },
          {
            id: 'landsat-9',
            name: 'Landsat 9',
            description: 'Landsat 9 Collection 2 Tier 1 surface reflectance',
            collection: 'LANDSAT/LC09/C02/T1_L2',
            bands: ['SR_B2', 'SR_B3', 'SR_B4', 'SR_B5'],
            startDate: '2021-10-31',
            cloudFilter: 20
          },
          {
            id: 'modis',
            name: 'MODIS',
            description: 'MODIS Terra Surface Reflectance 8-Day Global 500m',
            collection: 'MODIS/006/MOD09A1',
            bands: ['sur_refl_b01', 'sur_refl_b04', 'sur_refl_b03'],
            startDate: '2000-02-18',
            cloudFilter: 10
          },
          {
            id: 'spot',
            name: 'SPOT',
            description: 'SPOT VEGETATION V2',
            collection: 'VITO/PROBAV/C1/S1_TOC_100M',
            bands: ['RED', 'NIR', 'BLUE'],
            startDate: '2014-01-01',
            cloudFilter: 20
          },
          {
            id: 'sentinel-3',
            name: 'Sentinel-3',
            description: 'Sentinel-3 OLCI Level 2 Ocean Color',
            collection: 'COPERNICUS/S3/OLCI/L2_LAND',
            bands: ['Oa01_radiance', 'Oa02_radiance', 'Oa03_radiance', 'Oa04_radiance'],
            startDate: '2016-05-13',
            cloudFilter: 10
          },
          {
            id: 'sentinel-6',
            name: 'Sentinel-6',
            description: 'Sentinel-6 Michael Freilich Ocean Surface Topography',
            collection: 'COPERNICUS/S6/L2/ALT',
            bands: ['sea_surface_height_above_sea_level', 'significant_wave_height'],
            startDate: '2020-11-21',
            cloudFilter: 0
          },
          {
            id: 'landsat-10',
            name: 'Landsat 10',
            description: 'Landsat 10 Collection 3 Level 2 (2025)',
            collection: 'LANDSAT/LC10/C02/T1_L2',
            bands: ['SR_B2', 'SR_B3', 'SR_B4', 'SR_B5'],
            startDate: '2025-01-01',
            cloudFilter: 15
          },
          {
            id: 'modis-terra-2025',
            name: 'MODIS Terra 2025',
            description: 'MODIS Terra Surface Reflectance 2025 Enhanced',
            collection: 'MODIS/061/MOD09A1',
            bands: ['sur_refl_b01', 'sur_refl_b04', 'sur_refl_b03', 'sur_refl_b02'],
            startDate: '2025-01-01',
            cloudFilter: 5
          },
          {
            id: 'modis-aqua-2025',
            name: 'MODIS Aqua 2025',
            description: 'MODIS Aqua Surface Reflectance 2025 Enhanced',
            collection: 'MODIS/061/MYD09A1',
            bands: ['sur_refl_b01', 'sur_refl_b04', 'sur_refl_b03', 'sur_refl_b02'],
            startDate: '2025-01-01',
            cloudFilter: 5
          },
          {
            id: 'planet-skybox',
            name: 'Planet SkySat',
            description: 'Planet SkySat High Resolution Imagery',
            collection: 'PLANET/SKYBOX',
            bands: ['R', 'G', 'B', 'NIR'],
            startDate: '2020-01-01',
            cloudFilter: 10
          },
          {
            id: 'blacksky-constellation',
            name: 'BlackSky',
            description: 'BlackSky High Resolution Constellation',
            collection: 'BLACKSKY/BLACKSKY',
            bands: ['R', 'G', 'B', 'NIR'],
            startDate: '2021-01-01',
            cloudFilter: 15
          }
        ];
        setGeeCollections(fallbackCollections);
      }
    };
    
    loadGeeCollections();
  }, []);

  // Handle GEE collection selection
  const handleGeeCollectionSelect = (collectionId: string) => {
    if (collectionId) {
      setSelectedGeeCollection(collectionId);
      setShowDateSelector(true);
      
      // Convert collection ID to basemap type and set it
      let geeBasemapType: BasemapType;
      
      // Handle special cases for MODIS 2025 collections
      if (collectionId === 'modis-terra-2025') {
        geeBasemapType = 'gee-modis-terra-2025';
      } else if (collectionId === 'modis-aqua-2025') {
        geeBasemapType = 'gee-modis-aqua-2025';
      } else {
        geeBasemapType = `gee-${collectionId}` as BasemapType;
      }
      
      setBasemap(geeBasemapType);
    } else {
      setSelectedGeeCollection(null);
      setSelectedDateRange(null);
      // Reset to default basemap when no GEE collection is selected
      setBasemap('mapbox-streets');
    }
  };

  // Handle date range confirmation
  const handleDateRangeConfirm = (dateRange: { startDate: string; endDate: string }) => {
    setSelectedDateRange(dateRange);
    setShowDateSelector(false);
    // Here you would typically trigger the map to load the GEE imagery
    // For now, we'll just store the selection
  };

  // Handle animation button click
  const handleAnimationClick = () => {
    if (selectedGeeCollection && selectedDateRange) {
      setShowAnimation(true);
    }
  };

  // Get appropriate color for imported layer based on type
  const getImportedLayerColor = (filename: string): string => {
    const type = getLayerType(filename);
    switch (type) {
      case 'gpx': return '#FF6B35'; // Orange-red for GPX
      case 'kml': return '#4A90E2'; // Blue for KML
      case 'shp': case 'zip': return '#7ED321'; // Green for Shapefile
      case 'csv': return '#9013FE'; // Purple for CSV
      case 'geojson': case 'json': return '#F5A623'; // Yellow for GeoJSON
      default: return '#50E3C2'; // Teal for unknown
    }
  };

  // Define layer colors and order
  const layerConfig = [
    { id: 'couloirs', name: 'Couloirs', color: '#808080' }, // Grey
    { id: 'mejhars', name: 'Mejhars', color: '#e91e63' }, // Pink/Magenta
    { id: 'petite-mine', name: 'Permis petite mine', color: '#FFD700' }, // Gold (updated to match new styling)
    { id: 'zones-commerciales', name: 'Zones commerciales', color: '#800080' }, // Purple - now a single toggle
    { id: 'zones-categorie-f', name: 'Zones catégorie F', color: '#008080' }, // Teal
    { id: 'permis-industriels', name: 'Permis industriels', color: '#8B4513' }, // Brown
    { id: 'forages', name: 'Forages', color: '#87CEEB' }, // Light Blue
    { id: 'centres-traitement', name: 'Centres de traitement', color: '#FF6347' }, // Tomato Red
    { id: 'routes', name: 'Routes', color: '#FFD700' }, // Gold
    { id: 'bureaux-maaden', name: 'Bureaux MAADEN', color: '#4169E1' }, // Royal Blue
  ];

  return (
        <Paper
          elevation={3}
          className="glass-pane-light"
          style={{ filter: 'none' }}
          sx={{
            position: 'absolute',
            bottom: '20px',
            left: '10px',
        width: '240px', // Increased from 200px to fit longer names
        padding: '8px 0 6px 0',
            zIndex: 1100,
        background: 'rgba(0,0,0,0.96)', // Changed to black like header
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          pb: 0.8,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Layers sx={{ color: '#fff', fontSize: '1.1rem' }} />
          <Typography
            sx={{
              color: '#fff',
              fontSize: '0.85rem', // Decreased from larger size
              fontWeight: 600,
              fontFamily: 'Louguiya, sans-serif',
              letterSpacing: '0.5px',
            }}
          >
            Couches
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{
            color: '#fff',
            p: 0.5,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Box>

      <Collapse in={expanded}>
        <List sx={{ py: 0.5 }}>
          {/* Layer toggles - ordered with Couloirs at top */}
          {layerConfig.map((layer) => (
            <ListItem
              key={layer.id}
              sx={{
                minHeight: '32px', // Decreased height
                px: 1.5,
                py: 0.3,
                gap: 0.8,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.03)',
                },
              }}
            >
              <Box
                sx={{
                  width: '12px', // Decreased size
                  height: '12px', // Decreased size
                  borderRadius: '2px',
                  backgroundColor: layer.color,
                  border: '1px solid rgba(255,255,255,0.2)',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '0.7rem', // Decreased font size
                  fontWeight: 500,
                  fontFamily: 'Louguiya, sans-serif',
                  letterSpacing: '0.3px',
                  flex: 1,
                }}
              >
                {layer.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => toggleLayer(layer.id)}
                sx={{
                  color: visibleLayers.includes(layer.id) ? '#2c8fdb' : '#666',
                  p: 0.3,
                  '&:hover': { 
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    color: visibleLayers.includes(layer.id) ? '#90caf9' : '#999'
                  },
                }}
              >
                {visibleLayers.includes(layer.id) ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
              </IconButton>
            </ListItem>
          ))}

          {/* Labels Toggle */}
          <ListItem
            sx={{
              minHeight: '32px',
              px: 1.5,
              py: 0.3,
              gap: 0.8,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.03)',
              },
            }}
          >
            <Box
              sx={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: '#4CAF50', // Green for labels
                border: '1px solid rgba(255,255,255,0.2)',
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
              <Label sx={{ color: '#4CAF50', fontSize: '0.9rem' }} />
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  fontFamily: 'Louguiya, sans-serif',
                  letterSpacing: '0.3px',
                }}
              >
                Labels
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => setLabelsVisible(!labelsVisible)}
              sx={{
                color: labelsVisible ? '#2c8fdb' : '#666',
                p: 0.3,
                '&:hover': { 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: labelsVisible ? '#90caf9' : '#999'
                },
              }}
            >
              {labelsVisible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
            </IconButton>
          </ListItem>


        </List>
      </Collapse>

      {/* Imported Layers Section */}
      {importedLayers.length > 0 && (
        <>
          <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
          
          {/* Imported Layers Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              pb: 0.8,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CloudUpload sx={{ color: '#2c8fdb', fontSize: '1.1rem' }} />
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  fontFamily: 'Louguiya, sans-serif',
                  letterSpacing: '0.5px',
                }}
              >
                Imported Layers
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setImportedExpanded(!importedExpanded)}
              sx={{
                color: '#fff',
                p: 0.5,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {importedExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Box>

          <Collapse in={importedExpanded}>
            <List sx={{ py: 0.5 }}>
              {importedLayers.map((layer) => {
                const layerType = getLayerType(layer.name);
                const isGPX = layerType === 'gpx';
                const distance = isGPX ? calculateGPXDistance(layer.geojson) : null;
                
                return (
                  <ListItem
                    key={layer.id}
                    sx={{
                      minHeight: '32px',
                      px: 1.5,
                      py: 0.3,
                      gap: 0.8,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.03)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        backgroundColor: getImportedLayerColor(layer.name),
                        border: '1px solid rgba(255,255,255,0.2)',
                        flexShrink: 0,
                      }}
                    />
                    <Tooltip
                      title={
                        isGPX && distance 
                          ? `GPX Track - Distance: ${distance.toFixed(2)} km`
                          : `${layerType.toUpperCase()} layer`
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          fontFamily: 'Louguiya, sans-serif',
                          letterSpacing: '0.3px',
                          flex: 1,
                          cursor: 'default',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {layer.name}
                        {isGPX && distance && (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              color: '#FF6B35',
                              fontSize: '0.6rem',
                              fontWeight: 400,
                            }}
                          >
                            ({distance.toFixed(1)}km)
                          </Box>
                        )}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => onImportedLayerToggle?.(layer.id)}
                        sx={{
                          color: layer.visible ? '#2c8fdb' : '#666',
                          p: 0.3,
                          '&:hover': { 
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            color: layer.visible ? '#90caf9' : '#999'
                          },
                        }}
                      >
                        {layer.visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onImportedLayerDelete?.(layer.id)}
                        sx={{
                          color: '#ff4444',
                          p: 0.3,
                          '&:hover': { 
                            backgroundColor: 'rgba(255, 68, 68, 0.1)',
                            color: '#ff6666',
                          },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </>
      )}

      {/* Basemap Dropdown */}
      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
      <ListItem
        sx={{
          minHeight: '32px',
          px: 1.5,
          py: 0.5,
          gap: 0.8,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.03)',
          },
        }}
      >
        <Typography
          sx={{
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 500,
            fontFamily: 'Louguiya, sans-serif',
            letterSpacing: '0.3px',
            flex: 1,
          }}
        >
          Tiles:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={basemap}
            onChange={(e) => setBasemap(e.target.value as BasemapType)}
            size="small"
            sx={{
              color: '#fff',
              fontSize: '0.6rem',
              fontFamily: 'Louguiya, sans-serif',
              height: '28px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(44, 143, 219, 0.5)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#2c8fdb',
              },
              '& .MuiSelect-icon': {
                color: '#fff',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'rgba(0,0,0,0.95)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  '& .MuiMenuItem-root': {
                    color: '#fff',
                    fontSize: '0.6rem',
                    fontFamily: 'Louguiya, sans-serif',
                    '&:hover': {
                      backgroundColor: 'rgba(44, 143, 219, 0.1)',
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="mapbox-streets">
              <Tooltip title="Mapbox Streets with labels" arrow>
                <span>Mapbox Streets</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="ign-1993">
              <Tooltip title="IGN 1993 topographic map" arrow>
                <span>IGN 1993</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="mapbox-outdoors">
              <Tooltip title="Mapbox Outdoors with terrain" arrow>
                <span>Mapbox Outdoors</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="satellite-a">
              <Tooltip title="Landsat 8 satellite imagery with street labels" arrow>
                <span>Landsat 8</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="satellite-b">
              <Tooltip title="Sentinel-2 satellite imagery" arrow>
                <span>Sentinel-2</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="satellite-c">
              <Tooltip title="SPOT satellite imagery" arrow>
                <span>SPOT</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="satellite-enhanced">
              <Tooltip title="Enhanced satellite with ESRI World Imagery overlay for best resolution" arrow>
                <span>Enhanced SAT</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="maxar-worldview">
              <Tooltip title="Maxar WorldView - Ultra-high resolution (30cm) satellite imagery" arrow>
                <span>Maxar WorldView</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="airbus-pleiades">
              <Tooltip title="Airbus Pléiades Neo - Very high resolution (30cm) imagery" arrow>
                <span>Airbus Pléiades</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="planet-skybox">
              <Tooltip title="Planet SkySat - High resolution (50cm) daily monitoring" arrow>
                <span>Planet SkySat</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="blacksky-constellation">
              <Tooltip title="BlackSky - High resolution (1m) with frequent revisits" arrow>
                <span>BlackSky</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="capella-sar">
              <Tooltip title="Capella Space SAR - All-weather radar imagery" arrow>
                <span>Capella SAR</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="iceye-sar">
              <Tooltip title="ICEYE SAR - All-weather monitoring and surveillance" arrow>
                <span>ICEYE SAR</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="sentinel-3">
              <Tooltip title="Sentinel-3 - Ocean and land monitoring" arrow>
                <span>Sentinel-3</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="sentinel-6">
              <Tooltip title="Sentinel-6 - Ocean surface topography" arrow>
                <span>Sentinel-6</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="landsat-10">
              <Tooltip title="Landsat 10 - Latest Landsat satellite imagery (2025)" arrow>
                <span>Landsat 10</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="modis-terra-2025">
              <Tooltip title="MODIS Terra 2025 - Daily global land and atmosphere monitoring" arrow>
                <span>MODIS Terra 2025</span>
              </Tooltip>
            </MenuItem>
            <MenuItem value="modis-aqua-2025">
              <Tooltip title="MODIS Aqua 2025 - Daily global ocean and atmosphere monitoring" arrow>
                <span>MODIS Aqua 2025</span>
              </Tooltip>
            </MenuItem>
          </Select>
        </FormControl>
      </ListItem>

      {/* GEE Dropdown */}
      <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
      <ListItem
        sx={{
          minHeight: '32px',
          px: 1.5,
          py: 0.5,
          gap: 0.8,
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.03)',
          },
        }}
      >
        <Typography
          sx={{
            color: '#4CAF50',
            fontSize: '0.7rem',
            fontWeight: 500,
            fontFamily: 'Louguiya, sans-serif',
            letterSpacing: '0.3px',
            flex: 1,
          }}
        >
          GEE:
        </Typography>
        <Box
          sx={{
            minWidth: 120,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: 1,
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            color: '#4CAF50',
            fontStyle: 'italic',
            fontSize: '0.6rem',
            fontFamily: 'Louguiya, sans-serif',
            '&:hover': {
              borderColor: 'rgba(76, 175, 80, 0.5)',
              backgroundColor: 'rgba(76, 175, 80, 0.15)',
            },
          }}
        >
          Under Construction
        </Box>
      </ListItem>

      {/* GEE Controls */}
      {selectedGeeCollection && selectedDateRange && (
        <ListItem
          sx={{
            minHeight: '32px',
            px: 1.5,
            py: 0.5,
            gap: 0.8,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.03)',
            },
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            <Typography
              sx={{
                color: '#4CAF50',
                fontSize: '0.7rem',
                fontWeight: 500,
                fontFamily: 'Louguiya, sans-serif',
              }}
            >
              Active GEE: {geeCollections.find(c => c.id === selectedGeeCollection)?.name}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.6rem',
                fontFamily: 'Louguiya, sans-serif',
              }}
            >
              {new Date(selectedDateRange.startDate).toLocaleDateString()} - {new Date(selectedDateRange.endDate).toLocaleDateString()}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CalendarToday />}
                onClick={() => setShowDateSelector(true)}
                sx={{
                  fontSize: '0.6rem',
                  height: 24,
                  borderColor: 'rgba(76, 175, 80, 0.3)',
                  color: '#4CAF50',
                  '&:hover': {
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                  }
                }}
              >
                Change Dates
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleAnimationClick}
                sx={{
                  fontSize: '0.6rem',
                  height: 24,
                  bgcolor: '#4CAF50',
                  '&:hover': {
                    bgcolor: '#45a049',
                  }
                }}
              >
                Animate
              </Button>
            </Box>
          </Box>
        </ListItem>
      )}

      {/* GEE Date Range Selector */}
      <GEEDateRangeSelector
        open={showDateSelector}
        onClose={() => setShowDateSelector(false)}
        onConfirm={handleDateRangeConfirm}
        collectionId={selectedGeeCollection || ''}
        title={`Select Date Range for ${geeCollections.find(c => c.id === selectedGeeCollection)?.name}`}
      />

      {/* GEE Animation */}
      <GEEAnimation
        open={showAnimation}
        onClose={() => setShowAnimation(false)}
        collectionId={selectedGeeCollection || ''}
        dateRange={selectedDateRange || { startDate: '', endDate: '' }}
        bounds={undefined} // You would get this from the map bounds
      />
    </Paper>
  );
};

export default LayerControls;
